import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import ThreeDotsButton from '../../atoms/ThreeDotsButton';

const MENU_ITEM_SELECTOR = '[role="menuitem"]:not(:disabled)';

const getEnabledMenuItems = (menuElement) => (
    menuElement ? Array.from(menuElement.querySelectorAll(MENU_ITEM_SELECTOR)) : []
);

const focusMenuItem = (menuElement, nextIndex) => {
    const menuItems = getEnabledMenuItems(menuElement);
    if (menuItems.length === 0) {
        return;
    }
    const safeIndex = ((nextIndex % menuItems.length) + menuItems.length) % menuItems.length;
    menuItems[safeIndex].focus();
};

export default function CardActionsMenu({
    isOpen = false,
    onToggle = null,
    itemId,
    actions = [],
    disabled = false,
}) {
    const menuActions = Array.isArray(actions)
        ? actions.filter((action) => action && typeof action.onSelect === 'function')
        : [];
    const triggerRef = useRef(null);
    const menuRef = useRef(null);
    const pendingMenuFocusIndexRef = useRef(null);

    const closeMenu = useCallback(() => {
        if (!isOpen || !itemId || typeof onToggle !== 'function') {
            return;
        }
        onToggle(itemId);
        triggerRef.current?.focus?.();
    }, [isOpen, itemId, onToggle]);

    // Only a keyboard open pulls focus into the menu. `pendingMenuFocusIndexRef`
    // is the record of that intent: an index when the menu was opened from the
    // keyboard, null when a pointer opened it. Focusing item 0 regardless — as
    // this did — left the first item wearing a focus ring after a plain click,
    // because a programmatic focus() with no pointer interaction yet recorded
    // on the document still matches :focus-visible.
    useEffect(() => {
        if (isOpen) {
            const pendingFocusIndex = pendingMenuFocusIndexRef.current;
            pendingMenuFocusIndexRef.current = null;
            if (pendingFocusIndex != null) {
                focusMenuItem(menuRef.current, pendingFocusIndex);
            }
            return;
        }
        pendingMenuFocusIndexRef.current = null;
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen || typeof document === 'undefined') {
            return undefined;
        }

        const handleDocumentKeyDown = (event) => {
            if (event.defaultPrevented || event.key !== 'Escape') {
                return;
            }
            event.preventDefault();
            closeMenu();
        };

        document.addEventListener('keydown', handleDocumentKeyDown);
        return () => document.removeEventListener('keydown', handleDocumentKeyDown);
    }, [closeMenu, isOpen]);

    if (!itemId || menuActions.length === 0 || typeof onToggle !== 'function') {
        return null;
    }

    const handleTriggerKeyDown = (event) => {
        if (disabled || event.defaultPrevented) {
            return;
        }
        if (event.key === 'Escape' && isOpen) {
            event.preventDefault();
            event.stopPropagation();
            closeMenu();
            return;
        }
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            if (!isOpen) {
                pendingMenuFocusIndexRef.current = event.key === 'ArrowUp' ? -1 : 0;
                onToggle(itemId);
                return;
            }
            focusMenuItem(menuRef.current, event.key === 'ArrowUp' ? -1 : 0);
        }
    };

    const handleTriggerClick = (event) => {
        // Enter and Space on a button arrive as a click too. `detail` is the
        // click count, which is 0 for those and >= 1 for a real pointer press —
        // so it is what separates a keyboard open from a mouse one here.
        const openedFromKeyboard = event?.detail === 0;
        pendingMenuFocusIndexRef.current = openedFromKeyboard ? 0 : null;
        onToggle(itemId);
    };

    const handleMenuKeyDown = (event) => {
        if (event.defaultPrevented) {
            return;
        }

        const menuItems = getEnabledMenuItems(menuRef.current);
        const currentIndex = menuItems.indexOf(event.target);

        switch (event.key) {
        case 'Escape':
            event.preventDefault();
            event.stopPropagation();
            closeMenu();
            break;
        case 'ArrowDown':
            event.preventDefault();
            focusMenuItem(menuRef.current, currentIndex < 0 ? 0 : currentIndex + 1);
            break;
        case 'ArrowUp':
            event.preventDefault();
            focusMenuItem(menuRef.current, currentIndex < 0 ? menuItems.length - 1 : currentIndex - 1);
            break;
        case 'Home':
            event.preventDefault();
            focusMenuItem(menuRef.current, 0);
            break;
        case 'End':
            event.preventDefault();
            focusMenuItem(menuRef.current, menuItems.length - 1);
            break;
        default:
            break;
        }
    };

    return (
        <div className="post-card__actions post-actions">
            <ThreeDotsButton
                ref={triggerRef}
                onClick={handleTriggerClick}
                onKeyDown={handleTriggerKeyDown}
                aria-expanded={isOpen}
                aria-haspopup="menu"
                disabled={disabled}
            />
            {isOpen && (
                <div
                    ref={menuRef}
                    className="post-card__dropdown"
                    role="menu"
                    onKeyDown={handleMenuKeyDown}
                >
                    {menuActions.map((action) => (
                        <button
                            key={action.id}
                            type="button"
                            onClick={action.onSelect}
                            role="menuitem"
                            disabled={disabled || action.disabled === true}
                        >
                            {action.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

CardActionsMenu.propTypes = {
    isOpen: PropTypes.bool,
    onToggle: PropTypes.func,
    itemId: PropTypes.string,
    actions: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        onSelect: PropTypes.func.isRequired,
        disabled: PropTypes.bool,
    })),
    disabled: PropTypes.bool,
};
